import styled from 'styled-components';
import { FaSolidIconName, Icon, IconName } from '../Icons/Icon';
import React from 'react';
import { Label, Text } from '../../Typography/Text/Text';
import { breakpoint } from '../../Themes/screensize';
import { Heading1 } from '../Heading/Heading';
import { useTestId } from '../../hooks/useTestId';
import { IThemePalette } from '../../Themes/interfaces';
import { transparentize } from 'polished';

export const cardColours = (palette: IThemePalette) => ({
  card: {
    base: {
      boxShadow: transparentize(0.9, palette.primary.text[75])
    },
    small: {
      background: palette.neutral.gray[60]
    },
    large: {
      background: palette.neutral.white
    },
    header: {
      text: palette.neutral.white,
      background: palette.primary.first[100]
    }
  }
});

export interface ICardProps extends React.BaseHTMLAttributes<object> {
  icon?: IconName;
  fasIcon?: FaSolidIconName;
  label: string;
}

const CardBase = styled.div`
  box-shadow: 0 0.125rem 0.25rem ${({ theme }) => theme.colours.card.base.boxShadow};
  border-radius: 1.25rem;
  display: flex;
  align-items: center;
`;

export const Card = styled(CardBase)`
  margin: auto;
  height: auto;
  position: relative;
  max-width: 39rem;
  flex-direction: column;
  @media (max-width: ${breakpoint.mobile}) {
    max-width: 22rem;
  }
`;

export const CardHeader = styled.div`
  width: 100%;
  padding: 3rem 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background-color: ${({ theme }) => theme.colours.card.header.background};
  border-top-left-radius: 1.25rem;
  border-top-right-radius: 1.25rem;
  & > * {
    color: ${({ theme }) => theme.colours.card.header.text};
  }
  ${Heading1} {
    margin: 0;
  }
  ${Text} {
    margin-top: 1.5rem;
    text-align: center;
  }
  ${Label} {
    text-align: center;
    margin-top: 0.5rem;
    display: block;
  }
`;

export const CardContent = styled.div`
  padding: 3rem 5rem;
  flex-direction: row;
  text-align: center;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  box-sizing: border-box;
  @media (max-width: ${breakpoint.mobile}) {
    width: auto;
    padding: 2rem 1.5rem;
  }
`;

export const CardFooter = styled.div`
  padding: 3rem 5rem;
  flex-direction: column;
  align-items: center;
  text-align: center;
  @media (max-width: ${breakpoint.mobile}) {
    padding: 3rem 1.5rem;
  }
`;

const CardBaseSmall = styled(CardBase)`
  margin: 0.5625rem;
  height: 3.5rem;
  min-width: 12.5rem;
  width: 14rem;
  max-width: 14rem;
  border-radius: 0.25rem;
  background-color: ${({ theme }) => theme.colours.card.small.background};
  flex-direction: row;
  padding-left: 5%;
  align-items: center;
  @media (max-width: ${breakpoint.mobile}) {
    width: 19rem;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  margin-right: 7%;
`;

export const CardSmall: React.FC<ICardProps> = ({ icon, label, fasIcon, ...props }) => {
  const { generateTestId } = useTestId(props, 'CardSmall');

  return (
    <CardBaseSmall {...props} {...generateTestId()}>
      <ImageContainer {...generateTestId('ImageContainer')}>
        {(icon || fasIcon) && <Icon name={icon} fasName={fasIcon} {...generateTestId('Icon')} />}
      </ImageContainer>
      <Label {...generateTestId('Label')}>{label}</Label>
    </CardBaseSmall>
  );
};

export const CardLarge = styled(Card)`
  max-width: 39rem;
  background-color: ${({ theme }) => theme.colours.card.large.background};
  box-shadow: 0 0.125rem 0.25rem ${({ theme }) => theme.colours.card.base.boxShadow};
  border-radius: 1.25rem;
`;

export const CardSeparator = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colours.separator.border};
  width: 100%;
`;
